import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormGroupDirective, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Constants } from '../../../../../shared/interfaces/constants';
import { MatOption } from '@angular/material/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatSelectModule } from '@angular/material/select';
import { UtilsService } from '../../../../../shared/services/utils.service';
import { Subject, takeUntil } from 'rxjs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { SharedService } from '../../../../../shared/services/shared.service';
import { CommonModule } from '@angular/common';
import { TagsDropAssignService } from '../../../../../shared/services/tags-drop-assign.service';

@Component({
	selector: 'app-transcriptions-filters',
	standalone: true,
	imports: [
		CommonModule,
		FormsModule,
		ReactiveFormsModule,
		TranslateModule,
		MatCardModule,
		MatDividerModule,
		MatButtonModule,
		MatIconModule,
		MatTooltipModule,
		MatCheckboxModule,
		MatFormFieldModule,
		MatInputModule,
		MatDatepickerModule,
		MatNativeDateModule,
		MatSelectModule,
		MatProgressSpinnerModule,
	],
	templateUrl: './transcriptions-filters.component.html',
	styleUrl: './transcriptions-filters.component.scss',
})
export class TranscriptionsFiltersComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
	private onDestroy: Subject<any> = new Subject();
	@Input() role!: 'client';
	@Input() dataAdministrationType!: 'TRANSCRIPTIONS';
	@Input() resetFilters: any;
	@Input() externalFilters: any;
	@Input() apiLoading!: boolean;
	@Output() actionRequested: EventEmitter<any> = new EventEmitter();

	filtersFormGroup: FormGroup = new FormGroup({});
	transcriptionTypes = Constants.TRANSCRIPTION_TYPES;
	transcriptionLangs = Constants.TRANSCRIPTION_LANGS;
	@ViewChild('selectAllTranscriptionTypes') private selectAllTranscriptionTypes!: MatOption;

	DAYS_RANGE = 7;
	DATE_RANGE_LIMITS = {
		dateFrom: new Date(new Date().setDate(new Date().getDate() - this.DAYS_RANGE)),
		dateTo: new Date(),
	};

	filtersActive = false;

	// CLIPPING-592 - mirrors TranscriptionUtils.validateKeywordSearchFilter (backend). Keep the
	// two numbers in step with MIN_PREFIX_LETTERS_BEFORE_STAR / MAX_SEARCH_RANGE_DAYS there.
	private readonly MIN_PREFIX_LETTERS_BEFORE_STAR = 3;
	private readonly MAX_SEARCH_RANGE_DAYS = 31;

	isRecording = false;
	isProcessingRecording = false;
	private mediaRecorder: MediaRecorder | null = null;
	private audioChunks: Blob[] = [];
	private recordingStartTime: number = 0;
	recordingDuration: string = '00:00';
	private recordingTimer: any;

	constructor(
		private fb: FormBuilder,
		public utils: UtilsService,
		private sharedService: SharedService,
		public tagsDropAssign: TagsDropAssignService,
		private translate: TranslateService
	) {
		this.formInitializer();
	}

	ngOnInit(): void {
		// Set initial date range to ensure it's displayed in the UI (Issue 2 fix)
		this.filtersFormGroup.controls['createdAtFrom'].setValue(this.DATE_RANGE_LIMITS.dateFrom);
		this.filtersFormGroup.controls['createdAtTo'].setValue(this.DATE_RANGE_LIMITS.dateTo);

		this.sharedService.requestActionInComponent.pipe(takeUntil(this.onDestroy)).subscribe({
			next: response => {
				if (response.componentName === 'TranscriptionsFiltersComponent') {
					switch (response.action) {
						case 'FILTER_TRIGGER': {
							this.filterListItems();
							break;
						}
					}
				}
			},
		});
	}

	ngAfterViewInit(): void {
		setTimeout(() => {
			// Type filter is hidden for clients; guard the ViewChild.
			this.selectAllTranscriptionTypes?.select();
			this.transcriptionTypeSelection();
		}, 1000);
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['resetFilters'] && !changes['resetFilters'].currentValue) {
			this.filtersActive = false;
			this.resetForm();
		}

		if (changes['externalFilters'] && changes['externalFilters'].currentValue) {
			const hasFilters = Object.keys(changes['externalFilters'].currentValue).some(
				key => changes['externalFilters'].currentValue[key] !== null && changes['externalFilters'].currentValue[key] !== undefined
			);
			if (hasFilters) {
				this.filtersFormGroup.patchValue(this.externalFilters);
				this.filterListItems();
			}
		}
	}

	ngOnDestroy(): void {
		this.onDestroy.next('');
		if (this.recordingTimer) {
			clearInterval(this.recordingTimer);
		}
	}

	private formInitializer() {
		this.filtersFormGroup = this.fb.group({
			types: [[]],
			langs: [this.transcriptionLangs.map(lang => lang.id)],
			text: [],
			speaker: [],
			tagIds: [[]],
			createdAtFrom: [this.DATE_RANGE_LIMITS.dateFrom],
			createdAtTo: [this.DATE_RANGE_LIMITS.dateTo],
		});
	}

	resetForm(formDirective?: FormGroupDirective) {
		this.filtersFormGroup.reset();
		if (formDirective) {
			formDirective.resetForm();
		}

		this.filtersFormGroup.controls['types'].setValue([]);
		this.filtersFormGroup.controls['langs'].setValue(this.transcriptionLangs.map(lang => lang.id));
		if (this.selectAllTranscriptionTypes) {
			this.selectAllTranscriptionTypes.select();
			this.transcriptionTypeSelection();
		}

		this.filtersFormGroup.controls['createdAtFrom'].setValue(this.DATE_RANGE_LIMITS.dateFrom);
		this.filtersFormGroup.controls['createdAtTo'].setValue(this.DATE_RANGE_LIMITS.dateTo);

		// Restore original preselected tags for client role (Issue 3 fix)
		this.sharedService.requestActionInComponent.next({
			componentName: 'TagsComponent',
			action: 'RESELECT_ORIGINAL_TAGS',
		});

		this.filterListItems();
	}

	getFiltersFormData() {
		const formData = this.filtersFormGroup.value;

		let createdAtFrom = this.filtersFormGroup.controls['createdAtFrom'].value;
		if (createdAtFrom) {
			createdAtFrom = new Date((createdAtFrom as Date).setHours(0, 0, 0)).getTime();
		}

		let createdAtTo = this.filtersFormGroup.controls['createdAtTo'].value;
		if (createdAtTo) {
			createdAtTo = new Date((createdAtTo as Date).setHours(0, 0, 0));
			createdAtTo = new Date(new Date(createdAtTo).setDate(new Date(createdAtTo).getDate() + 1)).getTime();
		}

		formData.createdAtFrom = createdAtFrom;
		formData.createdAtTo = createdAtTo;

		return this.filtersFormGroup.value;
	}

	getDateLimitRange() {
		return this.filtersFormGroup.controls['createdAtTo'].value;
	}

	transcriptionTypeSelection(itemSelected?: Event) {
		// Type filter is hidden for clients; nothing to toggle when the control isn't rendered.
		if (!this.selectAllTranscriptionTypes) {
			return;
		}
		if (itemSelected) {
			if (this.selectAllTranscriptionTypes.selected) {
				this.selectAllTranscriptionTypes.deselect();
			} else if (this.filtersFormGroup.controls['types'].value.length === this.transcriptionTypes.length) {
				this.selectAllTranscriptionTypes.select();
			}
		} else {
			this.filtersFormGroup.controls['types'].patchValue(this.selectAllTranscriptionTypes.selected ? ['ALL', ...this.transcriptionTypes.map(item => item.id)] : [], 0);
		}
	}

	filterListItems() {
		this.filtersActive = false;

		let createdAtFrom = this.filtersFormGroup.controls['createdAtFrom'].value;
		if (createdAtFrom) {
			createdAtFrom = new Date((createdAtFrom as Date).setHours(0, 0, 0)).getTime();
		}

		let createdAtTo = this.filtersFormGroup.controls['createdAtTo'].value;
		if (createdAtTo) {
			createdAtTo = new Date((createdAtTo as Date).setHours(0, 0, 0));
			createdAtTo = new Date(new Date(createdAtTo).setDate(new Date(createdAtTo).getDate() + 1)).getTime();
		}

		if (this.tagsDropAssign.manuallyselectedTags) {
			this.filtersFormGroup.controls['tagIds'].setValue(this.tagsDropAssign.manuallyselectedTags.map(tag => tag.id));
		} else {
			this.filtersFormGroup.controls['tagIds'].setValue([]);
		}

		const filterDto = JSON.parse(JSON.stringify(this.filtersFormGroup.value));
		filterDto.createdAtFrom = createdAtFrom;
		filterDto.createdAtTo = createdAtTo;

		if (this.filtersFormGroup.controls['types'].value) {
			filterDto.types = this.filtersFormGroup.controls['types'].value.find((item: string) => item === 'ALL') ? [] : this.filtersFormGroup.controls['types'].value;
		}

		const validationError = this.validateSearchFilter(filterDto.createdAtFrom, filterDto.createdAtTo, filterDto.text);
		if (validationError) {
			this.sharedService.showNotification.next({ message: validationError, type: 'ERROR' });
			return;
		}

		this.filtersActive = !Object.keys(filterDto).every((key: string) => filterDto[key] === null || (filterDto[key] && filterDto[key].length === 0));

		this.actionRequested.emit({ action: 'FILTER', filters: filterDto, id: 0 });
	}

	// CLIPPING-592. Same three rules as TranscriptionUtils.validateKeywordSearchFilter (backend) -
	// checked here first so the user sees a clear message immediately instead of waiting on a
	// round-trip for something that was always going to be rejected with a 400.
	private validateSearchFilter(createdAtFrom: number | null, createdAtTo: number | null, text: string | null): string | null {
		if (!createdAtFrom || !createdAtTo) {
			return this.translate.instant('TRANSCRIPTIONS.SEARCH_DATE_RANGE_REQUIRED');
		}

		const hasText = !!text && text.trim().length > 0;

		// The 31-day cap only applies once there is actual search text - a plain date-range
		// browse (no text) keeps allowing any range, same as before CLIPPING-592.
		if (hasText) {
			const rangeDays = (createdAtTo - createdAtFrom) / (1000 * 60 * 60 * 24);
			if (rangeDays > this.MAX_SEARCH_RANGE_DAYS) {
				return this.translate.instant('TRANSCRIPTIONS.SEARCH_DATE_RANGE_TOO_LONG');
			}

			for (const word of text!.trim().split(/\s+/)) {
				if (word.endsWith('*') && word.length - 1 < this.MIN_PREFIX_LETTERS_BEFORE_STAR) {
					return this.translate.instant('TRANSCRIPTIONS.SEARCH_PREFIX_TOO_SHORT', { word });
				}
			}
		}

		return null;
	}

	openCreateTranscriptionDialog() {
		this.actionRequested.emit({ action: 'CREATE_TRANSCRIPTION' });
	}

	private formatDuration(seconds: number): string {
		const minutes = Math.floor(seconds / 60);
		const remainingSeconds = seconds % 60;
		return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
	}

	private updateRecordingDuration() {
		if (this.isRecording && this.recordingStartTime) {
			const elapsedSeconds = Math.floor((Date.now() - this.recordingStartTime) / 1000);
			this.recordingDuration = this.formatDuration(elapsedSeconds);
		}
	}

	async toggleRecording() {
		if (this.isRecording) {
			this.stopRecording();
		} else {
			await this.startRecording();
		}
	}

	private async startRecording() {
		try {
			const stream = await navigator.mediaDevices.getUserMedia({
				audio: {
					sampleRate: 44100,
					channelCount: 2,
					echoCancellation: true,
					noiseSuppression: true
				}
			});

			this.audioChunks = [];
			this.mediaRecorder = new MediaRecorder(stream, {
				mimeType: MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
					? 'audio/webm;codecs=opus'
					: 'audio/webm'
			});

			this.mediaRecorder.ondataavailable = (event) => {
				if (event.data.size > 0) {
					this.audioChunks.push(event.data);
				}
			};

			this.mediaRecorder.onstop = () => {
				this.processRecording();
				stream.getTracks().forEach(track => track.stop());
			};

			this.mediaRecorder.start(1000);
			this.isRecording = true;
			this.recordingStartTime = Date.now();
			this.recordingDuration = '00:00';

			this.recordingTimer = setInterval(() => {
				this.updateRecordingDuration();
			}, 1000);
		} catch (error: any) {
			this.sharedService.showNotification.next({
				message: `${this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR')} \n ${error.error?.message || error.message}`,
				type: 'ERROR',
			});
		}
	}

	private stopRecording() {
		if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
			this.mediaRecorder.stop();
			this.isRecording = false;
		}
		if (this.recordingTimer) {
			clearInterval(this.recordingTimer);
			this.recordingTimer = null;
		}
	}

	private async processRecording() {
		if (this.audioChunks.length === 0) {
			return;
		}

		this.isProcessingRecording = true;

		try {
			const audioBlob = new Blob(this.audioChunks, {
				type: this.mediaRecorder?.mimeType || 'audio/webm'
			});

			const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
			const fileName = `recording-${timestamp}.webm`;
			const audioFile = new File([audioBlob], fileName, {
				type: audioBlob.type
			});

			await this.uploadAndTranscribeRecording(audioFile);

		} catch (error: any) {
			this.sharedService.showNotification.next({
				message: `${this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR')} \n ${error.error?.message || error.message}`,
				type: 'ERROR',
          	});
		} finally {
			this.isProcessingRecording = false;
			this.audioChunks = [];
		}
	}

	private async uploadAndTranscribeRecording(audioFile: File) {
		this.actionRequested.emit({
			action: 'CREATE_TRANSCRIPTION_FROM_RECORDING',
			audioFile: audioFile
		});
	}
}